import { useState, useRef, useEffect } from "react";
import { Bookmark, Home, Briefcase, MoreHorizontal, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

/**
 * SavePlaceChip
 * ─────────────
 * Compact "☆ Save" pill that appears next to a picked address (pickup or
 * dropoff). Tapping opens a mini popover with Home / Work / Other buttons.
 * If the address is already saved, we show a green ✓ label so the rider
 * doesn't duplicate favorites.
 *
 * Props:
 *  - address, lat, lng   → the place to save
 *  - existing            → array of the user's current favorites (to detect dupes)
 *  - onSaved             → called after a successful save so parent can refresh
 */
export default function SavePlaceChip({ address, lat, lng, existing = [], onSaved, testid = "save-place" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  if (!address || !lat || !lng) return null;

  // Match on either exact address or (lat,lng) within ~10m to avoid duplicates
  // from tiny formatting differences returned by Nominatim.
  const already = existing.find((f) => {
    if (f.address && f.address === address) return true;
    if (f.lat && f.lng) {
      const dLat = Math.abs(f.lat - lat);
      const dLng = Math.abs(f.lng - lng);
      return dLat < 0.0001 && dLng < 0.0001;
    }
    return false;
  });

  if (already) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-electric-teal bg-electric-teal/10 border border-electric-teal/30 rounded-full px-2 py-0.5"
        data-testid={`${testid}-already`}
        title={`Saved as ${already.label}`}
      >
        <Check size={10} strokeWidth={3} /> {already.label}
      </span>
    );
  }

  const save = async (label) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/favorites", { label, address, lat, lng });
      toast.success(`Saved as ${label}`);
      if (onSaved) onSaved();
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-white/60 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-2 py-0.5 transition"
        data-testid={testid}
        aria-label="Save this place"
      >
        <Bookmark size={10} strokeWidth={2.5} /> Save
      </button>
      {open && (
        <div
          className="absolute end-0 top-full mt-1 z-[60] bg-obsidian border border-white/15 rounded-xl shadow-[0_16px_48px_-8px_rgba(0,0,0,0.9)] overflow-hidden min-w-[140px]"
          data-testid={`${testid}-popover`}
        >
          {[
            { label: "Home", Icon: Home },
            { label: "Work", Icon: Briefcase },
            { label: "Other", Icon: MoreHorizontal },
          ].map(({ label, Icon }) => (
            <button
              key={label}
              type="button"
              disabled={busy}
              onClick={() => save(label)}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/5 transition text-start disabled:opacity-50"
              data-testid={`${testid}-as-${label.toLowerCase()}`}
            >
              <Icon size={13} className="text-electric-teal" />
              <span className="text-[12px] font-semibold text-white">{label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
